/**
 * Per-clip CHROMA-KEY (green-screen removal) — shared by preview + export. Runs entirely client-side
 * via getImageData/putImageData (CPU, works in Canvas2D + OffscreenCanvas). The renderer draws the
 * clip's frame onto a TRANSPARENT scratch canvas, calls `applyChromaKey` on that scratch, then blits
 * the scratch over the main canvas — so keyed pixels reveal the layers beneath (not black). NO backend.
 */

type AnyCtx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface ChromaParams {
  /** Key color to remove, e.g. '#00d400'. */
  color: string;
  /** 0..1 — how close a pixel must be to the key to drop (distance threshold). */
  similarity?: number;
  /** 0..1 — soft edge band above the threshold. */
  smoothness?: number;
  /** 0..1 — green-spill suppression on kept pixels. */
  spill?: number;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let h = hex.trim();
  if (h[0] === '#') h = h.slice(1);
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return null;
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}

/** Key out `c.color` across the WHOLE canvas of `ctx` (expected to hold only this clip's frame). */
export function applyChromaKey(ctx: AnyCtx, c: ChromaParams): void {
  const cv = ctx.canvas as { width: number; height: number };
  const w = cv.width | 0, h = cv.height | 0;
  if (w <= 0 || h <= 0) return;
  const key = hexToRgb(c.color);
  if (!key) return;

  const MAXD = 441.6729559; // sqrt(255^2*3)
  const t0 = (c.similarity ?? 0.4) * MAXD;
  const t1 = t0 + (c.smoothness ?? 0.1) * MAXD + 1;
  const spill = c.spill ?? 0.3;

  let img: ImageData;
  try { img = ctx.getImageData(0, 0, w, h); } catch { return; }
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    const dr = d[i] - key.r, dg = d[i + 1] - key.g, db = d[i + 2] - key.b;
    const dist = Math.sqrt(dr * dr + dg * dg + db * db);
    if (dist <= t0) {
      d[i + 3] = 0;
      continue;
    } else if (dist < t1) {
      d[i + 3] = Math.round(d[i + 3] * ((dist - t0) / (t1 - t0)));
    }
    // Spill suppression: pull green toward the red/blue average where it dominates.
    if (spill > 0 && d[i + 3] > 0) {
      const g = d[i + 1], avg = (d[i] + d[i + 2]) * 0.5;
      if (g > avg) d[i + 1] = Math.round(g - (g - avg) * spill);
    }
  }
  ctx.putImageData(img, 0, 0);
}
